import {
  ChartPieIcon,
  CheckBadgeIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import { AuthenticatedRoute } from '@/app/constant';
import { LeftNav } from '@/components/chariot-ui';
import {
  Assets as AssetsIcon,
  Attacks as AttacksIcon,
  Integrations as IntegrationsIcon,
  Jobs as JobsIcon,
  Seeds as SeedsIcon,
  Settings as SettingsIcon,
  ThreatIntel as ThreatIntelIcon,
  Vulnerabilities as VulnerabilitiesIcon,
} from '@/components/chariot-ui';
import { AgentsIcon } from '@/components/icons';
import { useGlobalState } from '@/state/global.state';
import { KeyboardListersId } from '@/types';
import { generatePathWithSearch } from '@/utils/url.util';

type NavItem = {
  icon: React.ReactElement;
  label: string;
  path: string;
  linkTo?: (children: React.ReactNode) => React.ReactElement;
  selected?: boolean;
  disabled?: boolean;
  tooltip?: string;
};

const mainNavItems: NavItem[] = [
  {
    icon: <ChartPieIcon className="size-5" />,
    label: 'Insights',
    path: AuthenticatedRoute.INSIGHTS,
  },
  {
    icon: <SeedsIcon />,
    label: 'Seeds',
    path: AuthenticatedRoute.SEEDS,
  },
  {
    icon: <AssetsIcon />,
    label: 'Assets',
    path: AuthenticatedRoute.ASSETS,
  },
  {
    icon: <GlobeAltIcon className="size-5" />,
    label: 'Technologies',
    path: AuthenticatedRoute.TECHNOLOGIES,
  },
  {
    icon: <VulnerabilitiesIcon />,
    label: 'Vulnerabilities',
    path: AuthenticatedRoute.VULNERABILITIES,
  },
  {
    icon: <ThreatIntelIcon />,
    label: 'Threats',
    path: AuthenticatedRoute.THREATS,
  },
  {
    icon: <AttacksIcon />,
    label: 'Attacks',
    path: AuthenticatedRoute.ATTACKS,
  },
  {
    icon: <CheckBadgeIcon className="size-5" />,
    label: 'Validations',
    path: AuthenticatedRoute.VALIDATIONS,
  },
];

const footerNavItems: NavItem[] = [
  {
    icon: <IntegrationsIcon />,
    label: 'Integrations',
    path: AuthenticatedRoute.INTEGRATIONS,
  },
  {
    icon: <AgentsIcon className="size-5" />,
    label: 'Agents',
    path: AuthenticatedRoute.AGENTS,
  },
  {
    icon: <JobsIcon />,
    label: 'Jobs',
    path: AuthenticatedRoute.JOBS,
  },
  {
    icon: <DocumentTextIcon className="size-5" />,
    label: 'Files',
    path: AuthenticatedRoute.FILES,
  },
  {
    icon: <SettingsIcon />,
    label: 'Settings',
    path: AuthenticatedRoute.SETTINGS,
  },
];

export const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { keyboardListeners } = useGlobalState();
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    const navItems = [...mainNavItems, ...footerNavItems];
    keyboardListeners.add(
      KeyboardListersId.NAVIGATION,
      {
        ArrowDown: () => {
          const currentRoute = location.pathname.split('/')[2];
          const currentIndex = navItems.findIndex(
            item => item.path === currentRoute
          );
          const nextIndex =
            currentIndex + 1 < navItems.length ? currentIndex + 1 : 0;
          navigate(
            generatePathWithSearch({
              pathname: navItems[nextIndex].path,
            })
          );
        },
        ArrowUp: () => {
          const currentRoute = location.pathname.split('/')[2];
          const currentIndex = navItems.findIndex(
            item => item.path === currentRoute
          );
          const nextIndex =
            currentIndex > 0 ? currentIndex - 1 : navItems.length - 1;
          navigate(
            generatePathWithSearch({
              pathname: navItems[nextIndex].path,
            })
          );
        },
      },
      true
    );

    return () => {
      keyboardListeners.remove(KeyboardListersId.NAVIGATION);
    };
  }, [location.pathname, navigate]);

  return (
    <div className="border-chariot-stroke relative z-20 border-r">
      <button
        aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        className={
          'border-chariot-stroke bg-chariot-background-primary absolute top-4 right-[-27px] z-30 flex h-10 w-7 items-center justify-center rounded-r-full border border-l-0 shadow transition-all duration-200'
        }
        onClick={() => setCollapsed(c => !c)}
        tabIndex={0}
        style={{ padding: 0 }}
      >
        <span
          className={
            'text-chariot-stroke transition-transform duration-300' +
            (collapsed ? ' rotate-0' : ' -rotate-180')
          }
        >
          <ChevronRightIcon className="size-5" />
        </span>
      </button>
      <LeftNav
        collapsed={collapsed}
        brandLogo={
          <img
            src="/icons/praetorian-icon.svg"
            alt="Praetorian"
            className="size-full"
          />
        }
        footerNavItems={getItems(footerNavItems, location)}
        footerTitle="Administration"
        mainNavItems={getItems(mainNavItems, location)}
      />
    </div>
  );
};

const getItems = (
  navItems: NavItem[],
  location: ReturnType<typeof useLocation>
) => {
  return navItems.map(item => {
    const isSelected = location.pathname.split('/')[2] === item.path;

    return {
      ...item,
      linkTo: (children: React.ReactNode) => (
        <Link to={item.path} relative="path">
          {children}
        </Link>
      ),
      selected: isSelected,
    };
  });
};

Navigation.displayName = 'Navigation';
